import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FloatingPopupBarChartWrapperComponent } from '@app/main/main-content/floating-popup-manager/components/floating-popup-bar-chart-wrapper/floating-popup-bar-chart-wrapper.component';
import { FloatingPopupContentChartActionsComponent } from '@app/main/main-content/floating-popup-manager/components/floating-popup-content-chart-actions/floating-popup-content-chart-actions.component';
import { FloatingPopupContentExportCsvActionsComponent } from '@app/main/main-content/floating-popup-manager/components/floating-popup-content-export-csv-actions/floating-popup-content-export-csv-actions.component';
import { FpOpenCustomCorrelationActionComponent } from '@app/main/leak-detection/components/fp-open-custom-correlation-action/fp-open-custom-correlation-action.component';
import { IFloatingPopupInstance } from '@app/main/main-content/floating-popup-manager/interfaces/floating-popup-instance';
import { FloatingPopupService } from '@app/main/main-content/floating-popup-manager/services/floating-popup.service';
import { FpLoggerNoiseHistogramStore } from '@app/main/leak-detection/floating-popups/floating-popup-logger-noise/tabs/fp-logger-noise-histogram/services/fp-logger-noise-histogram/fp-logger-noise-histogram.store';
import { FpLoggerNoiseHistogramChartService } from '@app/main/leak-detection/floating-popups/floating-popup-logger-noise/tabs/fp-logger-noise-histogram/services/fp-logger-noise-histogram-chart.service';
import { FpLoggerNoiseHistogramExportService } from '@app/main/leak-detection/floating-popups/floating-popup-logger-noise/tabs/fp-logger-noise-histogram/services/fp-logger-noise-histogram-export.service';
import { FpLoggerNoiseHistogramActionType } from '@app/main/leak-detection/floating-popups/floating-popup-logger-noise/tabs/fp-logger-noise-histogram/services/fp-logger-noise-histogram/fp-logger-noise-histogram.actions';
import { FloatingPopupBaseErrorMessageComponent } from '@app/main/main-content/floating-popup-manager/components/floating-popup-base-error-message/floating-popup-base-error-message.component';

@Component({
  selector: 'gc-fp-logger-noise-histogram',
  standalone: true,
  imports: [
    FloatingPopupBarChartWrapperComponent,
    FloatingPopupContentChartActionsComponent,
    FloatingPopupContentExportCsvActionsComponent,
    FpOpenCustomCorrelationActionComponent,
    FloatingPopupBaseErrorMessageComponent,
  ],
  templateUrl: './fp-logger-noise-histogram.component.html',
  styleUrls: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'min-h-0 grow-1',
  },
})
export class FpLoggerNoiseHistogramComponent {
  public readonly currentTabStore = inject(FpLoggerNoiseHistogramStore);
  protected readonly chartService = inject(FpLoggerNoiseHistogramChartService);
  protected readonly exportService = inject(FpLoggerNoiseHistogramExportService);
  protected readonly floatingPopupService = inject(FloatingPopupService);

  public popup = input<IFloatingPopupInstance>();

  public isChartFullscreen = toSignal(this.currentTabStore.isChartFullscreenActive$, {
    initialValue: false,
  });

  private readonly popupMainData = toSignal(this.currentTabStore.popupMainData$, {
    initialValue: null,
  });

  private readonly data = toSignal(this.currentTabStore.data$, {
    initialValue: null,
  });

  public error = toSignal(this.currentTabStore.error$, {
    initialValue: null,
  });

  public isChartDataExist = computed(() => !!this.data()?.length);

  public readonly loggerDeployment = computed(() => this.popupMainData()?.deployment);

  public readonly chartPlugins = [this.chartService.getXTicksPlugin()];

  public combinedChartData = computed(() => {
    const data = this.data() || [];
    const chartData = this.chartService.getDataForChart(data);
    const chartOptions = this.chartService.generateOptions({ spread: data[0]?.spread ?? null });
    return { chartData, chartOptions };
  });

  public doExport() {
    const popupMainData = this.popupMainData();
    if (!popupMainData) return;
    this.exportService.exportCsv(popupMainData, this.data() || []);
  }

  public toggleChartFullscreen() {
    this.currentTabStore.dispatch({
      type: FpLoggerNoiseHistogramActionType.ToggleChartFullscreen,
      synchronizeWithPopupId: this.popup()?.id || undefined,
    });
  }
}
